<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.templates.focusPointTemplatesList.modal.new.buttonText')"
      @click="openFocusPointTemplateModal = true"
    />
    <export
      url="/focus_point_templates/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.templates.focusPointTemplates')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <FocusPointTemplateModal
    v-model:open="openFocusPointTemplateModal"
    v-model:focus-point-template="editedFocusPointTemplate"
    @saved="refetch"
  />
  <div class="main-area h-full">
    <focus-point-templates-map class="ml-12" v-if="records?.length" :busy="loading" :points="pointsForMap" />

    <div class="ml-12">
      <div class="text-body-regular-bold text-table-text-gray uppercase mb-5" data-v-67ba7ed1="">
        {{ $t('pages.campaigns.focusPointList.tableHeader') }}
      </div>
      <div class="table">
        <data-table
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          v-model:checked-ids-list="checkedIdsList"
          class="mb-auto"
          :busy="loading"
          :items="records"
          :columns="columns"
          :visible-columns="visibleColumns"
          :actions="actions"
          :choosable="true"
        />
      </div>
    </div>
  </div>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="tsx" setup>
import {computed, reactive, ref, unref} from 'vue';
import {useI18n} from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, {type Column} from '@utils/table/Table.vue';
import {collOptionsProvider, useProvider} from '@utils/table/hooks/provider';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';

import type {Filter} from '@utils/filter/Filter.vue';
import {useDelete} from '@hooks/api';
import Tabs from '@components/structure/navigation/Tabs.vue';
import FocusPointTemplateModal from '@pages/templates/FocusPointTemplateModal.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import {format} from 'date-fns';
import type {FocusPointTemplate, HydraCollection, Region} from '@/entities/Entities';
import {formatTimeDurationFromSeconds} from '@/utils/helpers';
import {useQueryRef} from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';
import FocusPointTemplatesMap from '@pages/templates/FocusPointTemplatesMap.vue';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 100);
const filter = useQueryRef('filter', {});

const openFocusPointTemplateModal = ref(false);
const editedFocusPointTemplate = ref();

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const [deleteFocusPointTemplate, { loading: deleting }] = useDelete();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.templates.focusPointTemplatesList.actions.edit'),
    action: (item: FocusPointTemplate) => {
      openFocusPointTemplateModal.value = true;
      editedFocusPointTemplate.value = { ...item };
    },
  },
  {
    title: t('pages.templates.focusPointTemplatesList.actions.delete'),
    action: async (item: FocusPointTemplate) => {
      if (await deleteFocusPointTemplate(`/focus_point_templates/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    group: t('pages.templates.focusPointTemplatesList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    filter: {
      type: 'text',
      group: t('pages.templates.focusPointTemplatesList.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'name',
    title: t('focusPointTemplate.name'),
    sortable: true,
    visible: true,
    group: t('pages.templates.focusPointTemplatesList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.templates.focusPointTemplatesList.filterGroups.general'),
      options: {
        label: t('focusPointTemplate.name'),
      },
    },
  },
  {
    key: 'region.name',
    title: t('focusPointTemplate.region'),
    group: t('pages.templates.focusPointTemplatesList.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'region',
      type: 'dropdown',
      group: t('pages.templates.focusPointTemplatesList.filterGroups.general'),
      options: {
        options: collOptionsProvider<Region>('/regions', {
          sort: {
            sortKey: 'name',
            sortDirection: 'ASC',
          },
        }, (value) => ({
          label: value.name,
          value: value.id,
        })),
        label: t('focusPointTemplate.region'),
      },
    },
  },
  {
    key: 'radius',
    title: t('focusPointTemplate.radius'),
    sortable: true,
    visible: true,
    group: t('pages.templates.focusPointTemplatesList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.templates.focusPointTemplatesList.filterGroups.general'),
      options: {
        icon: <span>m</span>,
        label: t('focusPointTemplate.radius'),
      },
    },
  },
  {
    key: 'gisLocation.lat',
    title: t('focusPointTemplate.gisLocation.latitude'),
    sortable: false,
    visible: true,
    group: t('pages.templates.focusPointTemplatesList.fieldGroups.general'),
  },
  {
    key: 'gisLocation.long',
    title: t('focusPointTemplate.gisLocation.longitude'),
    sortable: false,
    visible: true,
    group: t('pages.templates.focusPointTemplatesList.fieldGroups.general'),
  },
  {
    key: 'threshold',
    title: t('focusPointTemplate.threshold'),
    sortable: true,
    visible: true,
    group: t('pages.templates.focusPointTemplatesList.fieldGroups.general'),
    formatter: (v: number) => (v ? formatTimeDurationFromSeconds(v) : ''),
    filter: {
      type: 'duration',
      group: t('pages.templates.focusPointTemplatesList.filterGroups.general'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('focusPointTemplate.threshold'),
      },
    },
  },
  {
    key: 'cardinality',
    title: t('focusPointTemplate.cardinality'),
    sortable: true,
    visible: true,
    group: t('pages.templates.focusPointTemplatesList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.templates.focusPointTemplatesList.filterGroups.general'),
      options: {
        label: t('focusPointTemplate.cardinality'),
      },
    },
  },
  {
    key: 'paymentAmount',
    title: t('focusPointTemplate.paymentAmount'),
    sortable: true,
    visible: true,
    group: t('pages.templates.focusPointTemplatesList.fieldGroups.general'),
    unit: 'CHF',
    formatter: (v: number) => (v ? v.toFixed(2).padStart(5, '0') + ' CHF' : ''),
    filter: {
      type: 'range',
      group: t('pages.templates.focusPointTemplatesList.filterGroups.general'),
      options: {
        icon: <span>CHF</span>,
        label: t('focusPointTemplate.paymentAmount'),
      },
    },
  },
  {
    key: 'numOfPhotos',
    title: t('focusPointTemplate.numOfPhotos'),
    sortable: true,
    visible: true,
    group: t('pages.templates.focusPointTemplatesList.fieldGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.templates.focusPointTemplatesList.filterGroups.general'),
      options: {
        label: t('focusPointTemplate.numOfPhotos'),
      },
    },
  },
  {
    key: 'validityStart',
    title: t('focusPointTemplate.validityStart'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.templates.focusPointTemplatesList.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.templates.focusPointTemplatesList.filterGroups.general'),
      options: {
        label: t('focusPointTemplate.validityStart'),
      },
    },
  },
  {
    key: 'validityEnd',
    title: t('focusPointTemplate.validityEnd'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.templates.focusPointTemplatesList.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.templates.focusPointTemplatesList.filterGroups.general'),
      options: {
        label: t('focusPointTemplate.validityEnd'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<FocusPointTemplate>>('/focus_point_templates', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const loading = computed(() => loadingTable.value || deleting.value);

const checkedIdsList = ref<string[]>([]);
const pointsForMap = computed(() => checkedIdsList.value.map((id) => records.value?.find((record) => record.id === id)));
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
.medium {
  width: 300px;
  height: auto;
}
</style>

